import { css } from '@emotion/react';
import { Typography, Link } from '@mui/material';
import { Fragment, useLayoutEffect } from 'react';
import { useHistory, Link as RouterLink } from 'react-router-dom';

import { shouldBeEmail, shouldBePassword } from '@jebel/utils';

import { useAppAuth } from 'providers/useAppAuth';
import { PasswordField } from 'shared/components/form';
import { DividerOr } from 'shared/components/symbols';
import { Box, Button, Form, FormTextField, RouteLayout } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { useQueryParams, useToast } from 'shared/hooks';

import { SocialButtons } from '../components/SocialButtons';

const containerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const formBlock = css`
  display: grid;
  gap: 1rem;
`;

const bottomLinkWrap = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface RouteSearchParams {
  /**
   * The code to confirm social sign-in.
   * @see https://docs.amplify.aws/gen1/javascript/build-a-backend/auth/add-social-provider/#required-for-multi-page-applications-complete-social-sign-in-after-redirect
   */
  code?: string;
}

type FormData = {
  email?: string;
  password?: string;
};

export function AuthPage() {
  const [params] = useQueryParams<RouteSearchParams>();

  const { hasSession, login, confirmSocialSignIn } = useAppAuth();
  const { push: navigate } = useHistory();
  const { showError } = useToast();

  useLayoutEffect(() => {
    if (params.code) {
      confirmSocialSignIn(params.code);
    }

    if (!hasSession) {
      return;
    }

    navigate(ROUTES.user.home.index);
  }, [params.code, hasSession]);

  const onSubmit = async ({ email, password }: FormData) => {
    if (!email || !password) {
      return;
    }

    try {
      await login({ email, password });
      navigate(ROUTES.user.home.index);
    } catch (err) {
      showError('Something goes wrong when validating your credentials, please try again.');

      if (err instanceof Error) {
        showError(err.message);
        return;
      }
    }
  };

  return (
    <RouteLayout>
      <Box css={containerCSS}>
        <Typography variant="h2" align="center">
          Welcome! Login here
        </Typography>

        <Form oldCss={formBlock} onSubmit={onSubmit} validateOnBlur={false}>
          {({ isSubmitting }) => (
            <Fragment>
              <FormTextField
                label="Email Address"
                placeholder="myaccount@jebelapp.com"
                name="email"
                required
                inputMode="email"
                validate={shouldBeEmail}
              />

              <PasswordField
                name="password"
                label="Enter your password"
                placeholder="Enter your password"
                validate={shouldBePassword}
                autoComplete="password"
                required
              />

              <Button
                loading={isSubmitting}
                variant="contained"
                size="large"
                type="submit"
                color="secondary"
              >
                Login
              </Button>

              <DividerOr />

              <SocialButtons />

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  Forgot your password?&nbsp;
                </Typography>

                <Link component={RouterLink} to={ROUTES.public.recovery}>
                  Click here
                </Link>
              </Box>

              <Box css={bottomLinkWrap}>
                <Typography variant="inherit" align="inherit" color="initial" paragraph={false}>
                  No account?&nbsp;
                </Typography>

                <Link component={RouterLink} to={ROUTES.public.request}>
                  Request access
                </Link>
              </Box>
            </Fragment>
          )}
        </Form>
      </Box>
    </RouteLayout>
  );
}
